import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    AirspaceAuthorisationService,
    AirspaceCheckCommand,
    AirspaceCheckDto,
    AirspaceJurisdictionDto,
    DisplayableMissionDto,
    FieldError,
    FlyFreelyError,
    FlyFreelyLoggingService,
    InvalidOperation,
    JurisdictionAuthorisationOptionList,
    MissionIdentifier,
    MissionService,
    RequestAirspaceAuthorisationCommand,
    StateUpdateCommand,
    UserService,
    WorkTracker,
    patchState,
    patchableState,
    setState
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { deepEqual } from 'fast-equals';
import { findRpic } from 'libs/missions/src/lib/helpers';
import { ReplaySubject, Subject, combineLatest, forkJoin, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    AirspaceCheckService,
    AirspaceCheckStatus,
    incompleteOrInvalidAirspaceCheckerStatuses
} from '../airspace-check';
import {
    AirspaceAuthorisationFormValue,
    authorisationTypesWithOptions
} from './authorisation-preview/airspace-authorisation-preview.service';
import { ExternalAuthorisationProvider } from './external-authorisation-provider';
import { FlyFreelyAuthorisationProvider } from './flyfreely-authorisation-provider';
import {
    AlternativeAirspaceAuthorisationRequestCommand,
    AuthorisationError
} from './interfaces';

export interface AuthorisationAvailabilityState {
    status: 'AVAILABLE' | 'UNAVAILABLE';
    unavailableMessage?: string;
}

const invalidMissionTypesForAuthorisation: DisplayableMissionDto.Type[] = [
    'RETROSPECTIVE',
    'INSTANT_MISSION',
    'IMPORTED'
];

// ** State Interfaces **

interface CheckInputState {
    checkResult: {
        check: AirspaceCheckCommand;
        result: AirspaceCheckDto;
        missionId?: number;
    } | null;
    status: AirspaceCheckStatus;
    jurisdiction: AirspaceJurisdictionDto | null;
    externalBearerToken: { accessToken: string } | null;
    alternativeCheckerUrl: string | null;
}

const INITIAL_CHECK_INPUT_STATE: CheckInputState = {
    checkResult: null,
    status: 'INCOMPLETE',
    jurisdiction: null,
    externalBearerToken: null,
    alternativeCheckerUrl: null
};

interface MissionInputState {
    missionId: number | null;
    mission: DisplayableMissionDto | null;
    enabledAuthorisationTypes: AirspaceAuthorisationDto.AuthorisationType[];
}

const INITIAL_MISSION_INPUT_STATE: MissionInputState = {
    missionId: null,
    mission: null,
    enabledAuthorisationTypes: []
};

interface RequestState {
    availableAuthorisationTypes: AirspaceCheckDto.AvailableAutomatedAuthorisationList[];
    previewCommand: RequestAirspaceAuthorisationCommand | null;
    alternativeCommand: AlternativeAirspaceAuthorisationRequestCommand | null;
    jurisdictionAuthorisationOptions: JurisdictionAuthorisationOptionList | null;
    authorisationServiceAvailability: AuthorisationAvailabilityState;
}

const INITIAL_REQUEST_STATE: RequestState = {
    availableAuthorisationTypes: [],
    previewCommand: null,
    alternativeCommand: null,
    jurisdictionAuthorisationOptions: null,
    authorisationServiceAvailability: { status: 'AVAILABLE' }
};

interface ResultState {
    preview: AirspaceAuthorisationResponse | null;
    missionAuthorisations: AirspaceAuthorisationDto[] | null;
    relatedMissions: DisplayableMissionDto[];
    authorisationError: AuthorisationError | null;
    stateErrors: { [field: string]: FieldError } | null;
}

const INITIAL_RESULT_STATE: ResultState = {
    preview: null,
    missionAuthorisations: null,
    relatedMissions: [],
    authorisationError: null,
    stateErrors: null
};

/**
 * A scoped service that coordinates the management of airspace authorisations.
 *
 * State is organised into four `patchableState` slices:
 * - `checkInputState$`   - data received from AirspaceCheckService
 * - `missionInputState$` - mission context supplied by the hosting screen
 * - `requestState$`      - orchestration: available types, pending commands,
 *   loading flag, jurisdiction options
 * - `resultState$`       - output from preview / apply / fetch calls
 *
 * HTTP operations are fully delegated to two `AirspaceAuthorisationProvider`
 * implementations that are injected at component scope:
 *
 * - `FlyFreelyAuthorisationProvider`  - standard FlyFreely API
 * - `ExternalAuthorisationProvider`   - alternative external URL with bearer
 *   token (e.g. USA LAANC)
 *
 * The manager routes to the correct provider via `activeProvider` based on
 * whether an alternative URL is currently configured.
 */
@Injectable()
export class AirspaceAuthorisationManager {
    // ** State Sources **

    private checkInputStateSource = new Subject<
        StateUpdateCommand<CheckInputState>
    >();
    private missionInputStateSource = new Subject<
        StateUpdateCommand<MissionInputState>
    >();
    private requestStateSource = new Subject<
        StateUpdateCommand<RequestState>
    >();
    private resultStateSource = new Subject<StateUpdateCommand<ResultState>>();

    // ** State Observables **

    private checkInputState$ = patchableState(
        this.checkInputStateSource,
        INITIAL_CHECK_INPUT_STATE
    );
    private missionInputState$ = patchableState(
        this.missionInputStateSource,
        INITIAL_MISSION_INPUT_STATE
    );
    private requestState$ = patchableState(
        this.requestStateSource,
        INITIAL_REQUEST_STATE
    );
    private resultState$ = patchableState(
        this.resultStateSource,
        INITIAL_RESULT_STATE
    );

    // ** Outward Facing Observables **

    readonly availableAirspaceAuthorisations$ = this.requestState$.pipe(
        map(s => s.availableAuthorisationTypes),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    /**
     * The most recent complete airspace check result. Does not emit until at
     * least one check has completed.
     */
    readonly airspaceCheckResult$ = this.checkInputState$.pipe(
        filter(s => s.checkResult != null),
        map(s => s.checkResult),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly airspaceAuthorisationEnabled$ = this.checkInputState$.pipe(
        map(s => s.jurisdiction?.airspaceCheckSupport !== 'NONE'),
        distinctUntilChanged(),
        shareReplay(1)
    );

    readonly enabledAuthorisationTypes$ = this.missionInputState$.pipe(
        map(s => s.enabledAuthorisationTypes),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly missionId$ = this.missionInputState$.pipe(
        map(s => s.missionId),
        distinctUntilChanged(),
        shareReplay(1)
    );

    readonly currentMission$ = this.missionInputState$.pipe(
        map(s => s.mission),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly airspaceAuthorisationPreview$ = this.resultState$.pipe(
        map(s => s.preview),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly jurisdictionAuthorisationOptions$ = this.requestState$.pipe(
        map(s => s.jurisdictionAuthorisationOptions),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly missionAirspaceAuthorisations$ = this.resultState$.pipe(
        map(s => s.missionAuthorisations),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly authorisationRequestError$ = this.resultState$.pipe(
        map(s => s.authorisationError),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly authorisationStateErrors$ = this.resultState$.pipe(
        map(s => s.stateErrors),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly authorisationServiceUnavailable$ = this.requestState$.pipe(
        map(s => s.authorisationServiceAvailability),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    private workTracker = new WorkTracker();
    readonly checkingAuthorisation$ = this.workTracker.observable.pipe(
        shareReplay(1)
    );

    readonly relatedMissions$ = this.resultState$.pipe(
        map(s => s.relatedMissions),
        distinctUntilChanged(deepEqual),
        shareReplay(1)
    );

    readonly authorisationUrl$ = this.checkInputState$.pipe(
        map(s => s.alternativeCheckerUrl),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /** Only emitted after a successful `applyForAuthorisation` call. */
    private airspaceAuthorisationSubject =
        new ReplaySubject<AirspaceAuthorisationResponse>(1);
    airspaceAuthorisation$ = this.airspaceAuthorisationSubject.asObservable();

    // ** Other Variables **

    airspaceCheckCommand: AirspaceCheckCommand;

    savedMission: DisplayableMissionDto;

    /**
     * Holds the external URL if there was one in the last airspace check and drives
     * the alternative check URL behaviour.
     */
    private _alternativeUrl: string | null = null;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private airspaceCheckService: AirspaceCheckService,
        private airspaceAuthorisationService: AirspaceAuthorisationService,
        private missionService: MissionService,
        private logging: FlyFreelyLoggingService,
        private router: Router,
        private workspaceStateService: WorkspaceStateService,
        private flyFreelyProvider: FlyFreelyAuthorisationProvider,
        private externalProvider: ExternalAuthorisationProvider
    ) {
        this.checkingAuthorisation$ = this.workTracker.observable.pipe(
            shareReplay(1)
        );

        // Seed all state slices so derived observables emit immediately.
        // This prevents components stalling due to states not emitting.
        this.checkInputStateSource.next(setState(INITIAL_CHECK_INPUT_STATE));
        this.missionInputStateSource.next(
            setState(INITIAL_MISSION_INPUT_STATE)
        );
        this.requestStateSource.next(setState(INITIAL_REQUEST_STATE));
        this.resultStateSource.next(setState(INITIAL_RESULT_STATE));

        // ** State slice 1 setup **
        //
        // All five inputs arrive as a single patchState so no downstream
        // pipeline ever sees a partial update. The external provider is
        // configured here too so its cached URL/token are always current.
        combineLatest([
            airspaceCheckService.completeCheck$.pipe(
                distinctUntilChanged(deepEqual)
            ),
            airspaceCheckService.resultStatus$,
            airspaceCheckService.airspaceJurisdiction$,
            airspaceCheckService.externalAuthorisationBearerToken$,
            airspaceCheckService.alternativeCheckerUrl$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([
                    checkResult,
                    status,
                    jurisdiction,
                    externalBearerToken,
                    alternativeCheckerUrl
                ]) => {
                    if (checkResult != null) {
                        this.airspaceCheckCommand = checkResult.check;
                    }
                    // Keep the routing field and external provider in sync.
                    this._alternativeUrl = alternativeCheckerUrl;
                    this.externalProvider.configure(
                        alternativeCheckerUrl,
                        externalBearerToken
                    );

                    this.checkInputStateSource.next(
                        patchState({
                            checkResult,
                            status,
                            jurisdiction,
                            externalBearerToken,
                            alternativeCheckerUrl
                        })
                    );
                }
            );

        // ** State slice 2 setup **
        //
        // Watches both input slices together so check result and mission
        // details are always evaluated as a consistent pair.
        combineLatest([this.checkInputState$, this.missionInputState$])
            .pipe(
                distinctUntilChanged(deepEqual),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([checkInput, missionInput]) => {
                const { checkResult, status, jurisdiction } = checkInput;
                const { mission, enabledAuthorisationTypes } = missionInput;

                if (
                    incompleteOrInvalidAirspaceCheckerStatuses.includes(status)
                ) {
                    this.resultStateSource.next(patchState({ preview: null }));
                }

                const checkAuthorisationTypes =
                    checkResult?.result?.availableAutomatedAuthorisationList ??
                    [];
                const availableAuthorisationTypes: AirspaceCheckDto.AvailableAutomatedAuthorisationList[] =
                    checkAuthorisationTypes.reduce(
                        (acc, type) =>
                            !acc.includes(type) &&
                            enabledAuthorisationTypes?.includes(type)
                                ? acc.concat(type)
                                : acc,
                        [] as AirspaceCheckDto.AvailableAutomatedAuthorisationList[]
                    );

                const checksEnabled =
                    jurisdiction?.airspaceCheckSupport !==
                    AirspaceJurisdictionDto.AirspaceCheckSupport.NONE;

                if (
                    mission != null &&
                    mission.id != null &&
                    availableAuthorisationTypes.length > 0 &&
                    checksEnabled &&
                    !incompleteOrInvalidAirspaceCheckerStatuses.includes(
                        status
                    ) &&
                    checkResult != null
                ) {
                    this.requestStateSource.next(
                        patchState({ availableAuthorisationTypes })
                    );
                    this.buildPreviewCommand(
                        checkResult,
                        enabledAuthorisationTypes,
                        mission
                    );
                    this.fetchJurisdictionAuthorisationOptions(
                        availableAuthorisationTypes
                    );
                } else if (
                    !incompleteOrInvalidAirspaceCheckerStatuses.includes(status)
                ) {
                    // Only clear types when the check has COMPLETED and genuinely found nothing,
                    // not while a check is still in progress.
                    this.requestStateSource.next(
                        patchState({ availableAuthorisationTypes: [] })
                    );
                }
            });

        // ** State slice 3 setup **
        //
        // switchMap cancels any in-flight request when a new command arrives.
        // WorkTracker is created only when an actual HTTP call is made.
        this.requestState$
            .pipe(
                map(s => ({
                    previewCommand: s.previewCommand,
                    alternativeCommand: s.alternativeCommand
                })),
                distinctUntilChanged(deepEqual),
                switchMap(({ previewCommand, alternativeCommand }) => {
                    this.resultStateSource.next(patchState({ preview: null }));
                    if (
                        this._alternativeUrl != null &&
                        alternativeCommand != null
                    ) {
                        // Use the doneWorking pattern, calling done working in a finalize due to
                        // these calls occurring within the scope of long-running observables that
                        // shouldn't have work tracking.
                        const doneWorking = this.workTracker.createTracker();
                        return this.externalProvider
                            .getPreview(alternativeCommand)
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.handlePreviewError(error);
                                    return of(null);
                                }),
                                finalize(() => doneWorking())
                            );
                    }
                    if (
                        this._alternativeUrl == null &&
                        previewCommand != null
                    ) {
                        const doneWorking = this.workTracker.createTracker();
                        return this.flyFreelyProvider
                            .getPreview(previewCommand)
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.handlePreviewError(error);
                                    return of(null);
                                }),
                                finalize(() => doneWorking())
                            );
                    }
                    return of(null);
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: (result: AirspaceAuthorisationResponse) => {
                    // The external API embeds service availability in the
                    // preview response; surface it in request state.
                    if (result != null && this._alternativeUrl != null) {
                        this.requestStateSource.next(
                            patchState({
                                authorisationServiceAvailability: {
                                    // @ts-ignore - LAANC model extension
                                    status: result.authorisationServiceStatus,
                                    // @ts-ignore - LAANC model extension
                                    unavailableMessage:
                                        result.authorisationServiceMessage
                                }
                            })
                        );
                    }
                    this.findRelatedMissions(result);
                    if (result != null) {
                        this.resultStateSource.next(
                            patchState({ preview: result })
                        );
                    }
                }
            });

        // ** State slice 4 setup **
        this.checkInputState$
            .pipe(
                map(s => ({
                    token: s.externalBearerToken,
                    url: s.alternativeCheckerUrl
                })),
                filter(({ token, url }) => token != null && url != null),
                distinctUntilChanged(deepEqual),
                switchMap(() =>
                    this.externalProvider.findAllForMission(
                        AirspaceAuthorisationDto.AuthorisationType.USA_LAANC
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(authorisations => {
                this.resultStateSource.next(
                    patchState({ missionAuthorisations: authorisations })
                );
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.checkInputStateSource.complete();
        this.missionInputStateSource.complete();
        this.requestStateSource.complete();
        this.resultStateSource.complete();
        this.airspaceAuthorisationSubject.complete();
    }

    /**
     * Configure the authorisation types the hosting screen wants to enable and,
     * optionally, the current mission ID.
     */
    setup(
        enabledAuthorisationTypes: AirspaceAuthorisationDto.AuthorisationType[],
        missionId?: number
    ) {
        const patch: Partial<MissionInputState> = { enabledAuthorisationTypes };
        if (missionId !== undefined) {
            patch.missionId = missionId;
        }
        this.missionInputStateSource.next(patchState(patch));
    }

    updateMission(mission: DisplayableMissionDto) {
        this.savedMission = mission;
        this.missionInputStateSource.next(
            patchState({ missionId: mission.id, mission })
        );
        this.airspaceCheckService.updateSavedMission(mission);
        this.resetAuthorisationResults(mission);
    }

    /**
     * Clear all result data (preview, existing authorisations, errors) and
     * cancel any pending commands so the next `refreshAuthorisationPreview`
     * call triggers a fresh fetch.
     */
    resetAuthorisationResults(mission: DisplayableMissionDto) {
        this.resultStateSource.next(setState(INITIAL_RESULT_STATE));
        if (mission != null && mission.airspaceAuthorisationList?.length > 0) {
            this.resultStateSource.next(
                patchState({
                    missionAuthorisations: mission.airspaceAuthorisationList
                })
            );
        }
        this.requestStateSource.next(
            patchState({
                previewCommand: null,
                alternativeCommand: null
            })
        );
    }

    refreshAuthorisationPreview(
        command: RequestAirspaceAuthorisationCommand,
        authorisationFormValues?: AirspaceAuthorisationFormValue
    ) {
        if (this._alternativeUrl != null) {
            const alternativeCommand =
                this.buildAlternativeAuthorisationCommand(
                    command,
                    authorisationFormValues
                );
            this.requestStateSource.next(patchState({ alternativeCommand }));
        } else {
            this.requestStateSource.next(
                patchState({ previewCommand: command })
            );
        }
    }

    applyForAuthorisation(
        command: RequestAirspaceAuthorisationCommand,
        formValues?: AirspaceAuthorisationFormValue
    ) {
        if (this._alternativeUrl != null) {
            const alternativeCommand =
                this.buildAlternativeAuthorisationCommand(command, formValues);
            return this.externalProvider
                .apply(alternativeCommand)
                .pipe(
                    tap(() =>
                        this.refreshExistingMissionAuthorisations(
                            command.authorisationType
                        )
                    )
                );
        }
        return this.flyFreelyProvider
            .apply(command)
            .pipe(
                tap(missionAuthorisations =>
                    this.resultStateSource.next(
                        patchState({ missionAuthorisations })
                    )
                )
            );
    }

    findAuthorisation(
        id: string,
        authorisationType?: AirspaceAuthorisationDto.AuthorisationType
    ) {
        return this.activeProvider.findById(id, authorisationType);
    }

    cancelAuthorisation(
        id: string,
        authorisationType?: AirspaceAuthorisationDto.AuthorisationType
    ) {
        return this.activeProvider.cancel(id, authorisationType);
    }

    deleteAuthorisation(
        id: string,
        authorisationType?: AirspaceAuthorisationDto.AuthorisationType
    ) {
        return this.activeProvider.delete(id, authorisationType);
    }

    closeAuthorisation(
        id: string,
        authorisationType?: AirspaceAuthorisationDto.AuthorisationType
    ) {
        return this.activeProvider.close(id, authorisationType);
    }

    acknowledgeRescinded(
        id: string,
        authorisationType?: AirspaceAuthorisationDto.AuthorisationType
    ) {
        return this.activeProvider.acknowledgeRescinded(id, authorisationType);
    }

    /**
     * Build an `AlternativeAirspaceAuthorisationRequestCommand` from a
     * standard command, augmenting it with pilot details from the current
     * mission or the provided form values.
     */
    buildAlternativeAuthorisationCommand(
        command: RequestAirspaceAuthorisationCommand,
        authorisationFormValues: AirspaceAuthorisationFormValue
    ): AlternativeAirspaceAuthorisationRequestCommand {
        const rpic =
            authorisationFormValues?.rpic != null
                ? authorisationFormValues.rpic
                : this.savedMission != null &&
                  this.savedMission.missionCrewDetails.length > 0
                ? findRpic(this.savedMission.missionCrewDetails)
                : null;
        const rpicPhone =
            authorisationFormValues?.rpicPhone ?? rpic?.phoneNumber;
        const missionDetails = {
            ...this.airspaceCheckCommand,
            location: command.flightArea ?? this.airspaceCheckCommand.location
        };
        return {
            missionId: command.missionId,
            remotePilotPhoneNumber: rpicPhone,
            authorisationType: command.authorisationType,
            missionDetails,
            operatorId: this.userService.getCurrentUser().id,
            remotePilotEmail: rpic?.email,
            remotePilotFirstName: rpic?.firstName,
            remotePilotLastName: rpic?.lastName,
            // FIXME: LAANC model constraints
            additionalProviderProperties: command.additionalProviderProperties
        };
    }

    findAuthorisationsByMissionId(missionId: number) {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(
                switchMap(organisation =>
                    this.missionService.findMissionAirspaceAuthorisations(
                        missionId,
                        organisation.organisation.id
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: authorisations => {
                    this.resultStateSource.next(
                        patchState({ missionAuthorisations: authorisations })
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while finding authorisations for mission: ${error.message}`
                    );
                }
            });
    }

    refreshExistingMissionAuthorisations(
        authorisationType: AirspaceAuthorisationDto.AuthorisationType
    ) {
        if (this._alternativeUrl == null) {
            return;
        }
        this.externalProvider
            .findAllForMission(authorisationType)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authorisations =>
                    this.resultStateSource.next(
                        patchState({ missionAuthorisations: authorisations })
                    ),
                error: (error: AuthorisationError) => {
                    if ('code' in error) {
                        this.resultStateSource.next(
                            patchState({ authorisationError: error })
                        );
                    }
                }
            })
            .add(this.workTracker.createTracker());
    }

    goToRelatedMission(missionId: number) {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([`/missions/mission/${missionId}`], {
                queryParamsHandling: 'merge'
            })
        );
        window.open(url, '_blank');
    }

    // ** Private Helpers **

    /**
     * Returns the provider appropriate for the current configuration.
     * All read-only and action operations (findById, cancel, close, etc.) use
     * this. `applyForAuthorisation` has additional side-effect logic so it
     * accesses the providers directly.
     */
    private get activeProvider() {
        return this.externalProvider.isConfigured
            ? this.externalProvider
            : this.flyFreelyProvider;
    }

    /**
     * Handle errors from the preview API call inside the switchMap.
     * `InvalidOperation` errors carry per-field validation details that should
     * be surfaced in the UI rather than toasted; all other errors are logged.
     */
    private handlePreviewError(error: FlyFreelyError) {
        if (error instanceof InvalidOperation) {
            this.resultStateSource.next(
                patchState({
                    stateErrors: error.fieldErrors as {
                        [field: string]: FieldError;
                    }
                })
            );
        } else {
            this.logging.error(
                error,
                `Error while fetching authorisation preview: ${error.message}`
            );
        }
    }

    private buildPreviewCommand(
        results: {
            check: AirspaceCheckCommand;
            result: AirspaceCheckDto;
            missionId?: number;
        },
        enabledAuthorisationTypes: AirspaceAuthorisationDto.AuthorisationType[],
        mission: DisplayableMissionDto
    ) {
        const availableAuthorisationType = enabledAuthorisationTypes.find(at =>
            (
                results.result?.availableAutomatedAuthorisationList ?? []
            ).includes(at)
        );

        if (results.missionId != null && results.missionId !== mission.id) {
            this.missionInputStateSource.next(
                patchState({ missionId: results.missionId })
            );
        }

        if (
            mission.type == null ||
            invalidMissionTypesForAuthorisation.includes(mission.type) ||
            availableAuthorisationType == null
        ) {
            return;
        }

        if (!this.validateAuthorisationRequirements(mission)) {
            return;
        }

        const command: RequestAirspaceAuthorisationCommand = {
            missionId: mission.id,
            managingOrganisationId: this.savedMission.organisationId,
            authorisationType: availableAuthorisationType,
            flightArea: results.check.location
        };

        if (this._alternativeUrl != null) {
            const alternativeCommand =
                this.buildAlternativeAuthorisationCommand(command, null);
            this.requestStateSource.next(patchState({ alternativeCommand }));
        } else {
            this.requestStateSource.next(
                patchState({ previewCommand: command })
            );
        }
    }

    private validateAuthorisationRequirements(
        mission: DisplayableMissionDto
    ): boolean {
        const hasRpic =
            mission.missionCrewDetails != null &&
            mission.missionCrewDetails.some(
                crew =>
                    crew.role?.coreRole === 'PILOT_IN_COMMAND' &&
                    crew.person != null
            );
        const hasRuleSet =
            mission.missionWorkflowVersion?.delegatedAuthority?.authorityType
                ?.ruleset != null;
        const hasMaxHeight = mission?.maximumHeight != null;
        return hasRpic && hasRuleSet && hasMaxHeight;
    }

    private fetchJurisdictionAuthorisationOptions(
        authorisationTypes: AirspaceAuthorisationDto.AuthorisationType[]
    ) {
        const authorisationType = authorisationTypes[0];
        if (!authorisationTypesWithOptions.includes(authorisationType)) {
            return;
        }
        this.airspaceAuthorisationService
            .findJurisdictionAuthorisationOptionList(authorisationType)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: jurisdictionAuthorisationOptions =>
                    this.requestStateSource.next(
                        patchState({ jurisdictionAuthorisationOptions })
                    ),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while updating additional authorisation options: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private findRelatedMissions(preview: AirspaceAuthorisationResponse) {
        if (
            preview == null ||
            preview.authorisationRuleOutcomeList?.length === 0
        ) {
            return;
        }
        const outcomesWithRelatedMissions =
            preview.authorisationRuleOutcomeList.filter(
                outcome =>
                    outcome.relatedMissionList != null &&
                    outcome.relatedMissionList.length > 0
            );
        const relatedMissionList = outcomesWithRelatedMissions.reduce(
            (acc, outcome) => acc.concat(outcome.relatedMissionList),
            [] as MissionIdentifier[]
        );

        if (relatedMissionList.length === 0) {
            return;
        }

        // FIXME: handle failing calls when user has authorisations from
        // multiple organisations
        forkJoin(
            relatedMissionList.map(identifier =>
                this.missionService.findMission(
                    identifier.missionId,
                    identifier.organisationId
                )
            )
        )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missions => {
                this.resultState$
                    .pipe(
                        map(s => s.relatedMissions),
                        take(1)
                    )
                    .subscribe(fetchedMissions => {
                        const relatedMissions = fetchedMissions.concat(
                            missions.filter(
                                m =>
                                    fetchedMissions.find(
                                        mission => mission.id === m.id
                                    ) == null
                            )
                        );
                        this.resultStateSource.next(
                            patchState({ relatedMissions })
                        );
                    });
            });
    }
}
